import { FileAttachment } from "@/types/attachment";
import { getFileIcon } from "@/lib/attachmentHelpers";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface FileAttachmentCardProps {
  attachment: FileAttachment;
  onDelete?: (id: string) => void;
  onHighlight?: (id: string) => void;
  highlighted?: boolean;
}

export function FileAttachmentCard({
  attachment,
  onDelete,
  onHighlight,
  highlighted = false
}: FileAttachmentCardProps) {
  const FileIcon = getFileIcon(attachment.fileName);
  
  const colorBgClasses = {
    blue: 'bg-blue-500/5',
    green: 'bg-green-500/5',
    purple: 'bg-purple-500/5',
    orange: 'bg-orange-500/5',
    pink: 'bg-pink-500/5',
    yellow: 'bg-yellow-500/5',
    red: 'bg-red-500/5',
    cyan: 'bg-cyan-500/5',
  };

  const colorBorderClasses = {
    blue: 'border-l-4 border-blue-500',
    green: 'border-l-4 border-green-500',
    purple: 'border-l-4 border-purple-500',
    orange: 'border-l-4 border-orange-500',
    pink: 'border-l-4 border-pink-500',
    yellow: 'border-l-4 border-yellow-500',
    red: 'border-l-4 border-red-500',
    cyan: 'border-l-4 border-cyan-500',
  };

  const numberBgClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
    orange: 'bg-orange-500',
    pink: 'bg-pink-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
    cyan: 'bg-cyan-500',
  };

  return (
    <Card
      className={cn(
        "p-3 relative transition-all duration-300 cursor-pointer hover:shadow-md",
        colorBgClasses[attachment.color as keyof typeof colorBgClasses] || colorBgClasses.blue,
        colorBorderClasses[attachment.color as keyof typeof colorBorderClasses] || colorBorderClasses.blue,
        highlighted && "ring-2 ring-primary shadow-lg scale-[1.02]"
      )}
      onClick={() => onHighlight?.(attachment.id)}
    >
      {/* Number badge - square shape to distinguish from circular comment badges */}
      <div
        className={cn(
          "absolute left-2 top-3 w-6 h-6 rounded-sm flex items-center justify-center text-white text-xs font-bold",
          attachment.textSelections.length === 0
            ? 'bg-gray-500'
            : numberBgClasses[attachment.color as keyof typeof numberBgClasses] || numberBgClasses.blue
        )}
      >
        {attachment.textSelections.length === 0 ? 'G' : attachment.number}
      </div>

      <div className="ml-10">
        {attachment.textSelections.length > 0 && (
          <div className="mb-2 text-xs">
            <span className="font-semibold">[{attachment.number}]</span>{' '}
            <span className="italic">
              "{attachment.textSelections[0].text.split(' ').slice(0, 5).join(' ')}..."
            </span>
          </div>
        )}
        {attachment.textSelections.length === 0 && (
          <div className="text-xs font-semibold mb-2 opacity-70">
            [G] General Attachment
          </div>
        )}
        
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <FileIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <p className="text-sm font-medium truncate">{attachment.fileName}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-1">
            {attachment.dataUrl && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 hover:bg-primary/10"
                onClick={(e) => {
                  e.stopPropagation();
                  const link = document.createElement('a');
                  link.href = attachment.dataUrl!;
                  link.download = attachment.fileName;
                  link.click();
                }}
              >
                <Download className="h-3 w-3" />
              </Button>
            )}
            {onDelete && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-destructive hover:text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(attachment.id);
                }}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>
        
        {attachment.fileType === 'image' && attachment.dataUrl && (
          <div className="mt-2">
            <img 
              src={attachment.dataUrl} 
              alt={attachment.fileName}
              className="max-w-[120px] max-h-[120px] rounded border object-contain"
            />
          </div>
        )}
      </div>
    </Card>
  );
}
